import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Users, Phone, Settings, Plus, ChevronDown } from 'lucide-react';
import { toName } from './utils/variableUtils';
import {
    Variable,
    removeVariable,
    createVariable,
} from '../../services/api/departmentVariablesService';
import { useToast } from '../../hooks/use-toast';
import { VariableDefault, VariableSection } from './utils/variableTypes';
import { DepartmentUser } from './setup-wizard/WizardVariableForm';
import SectionCard from './SectionCard';
import VariableRow from './variable-row/VariableRow';
import { RefVariablePicker } from './variable-row/RefVariablePicker';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { EVENT_KEYS, trackClick } from '../../services/api/trackingService';

const SECTION_ICONS: Record<string, React.ReactNode> = {
    mriSafetyLeadership: <Users className="h-5 w-5" />,
    keyOperationalContacts: <Phone className="h-5 w-5" />,
    departmentInformation: <Home className="h-5 w-5" />,
    optionalOperationalFields: <Settings className="h-5 w-5" />,
};

interface SettingsOverviewProps {
    savedVariables: Variable[];
    users: DepartmentUser[];
    sections: VariableSection[];
    onSaved: (sectionKey?: string) => Promise<void>;
}

export default function SettingsOverview({
    savedVariables,
    users,
    sections,
    onSaved,
}: SettingsOverviewProps) {
    const { toast } = useToast();
    const { t } = useTranslation();

    const [showAddContact, setShowAddContact] = useState(false);
    const [contactInput, setContactInput] = useState('');
    const [contactRefId, setContactRefId] = useState<string | undefined>();
    const [contactFreeText, setContactFreeText] = useState('');
    const [showAddCustom, setShowAddCustom] = useState(false);
    const [customInput, setCustomInput] = useState('');

    const sectionByKey = useMemo(
        () =>
            Object.fromEntries(
                sections.map((section) => [section.key, section])
            ),
        [sections]
    );

    const defaultVariableNames = useMemo(
        () =>
            new Set(
                sections.flatMap((section) =>
                    section.variables.map((v) => v.name)
                )
            ),
        [sections]
    );

    const customVariables: VariableDefault[] = savedVariables
        .filter((variable) => !defaultVariableNames.has(variable.name))
        .map((variable) =>
            variable.type === 'ref'
                ? {
                      field: 'ref' as const,
                      name: variable.name,
                      label: variable.label,
                      refTarget: variable.refTarget ?? 'User.name',
                      removable: true,
                  }
                : {
                      field: 'text' as const,
                      name: variable.name,
                      label: variable.label,
                      removable: true,
                  }
        );

    const hasContactValue = !!contactRefId || !!contactFreeText.trim();

    const resetContactForm = () => {
        setContactInput('');
        setContactRefId(undefined);
        setContactFreeText('');
        setShowAddContact(false);
    };

    const handleAddContact = async () => {
        const label = contactInput.trim();
        if (!label || !hasContactValue) return;
        const name = toName(label);
        if (savedVariables.some((v) => v.name === name)) {
            toast({
                title: t('departmentVariables.errors.contactExists'),
                variant: 'destructive',
            });
            return;
        }
        try {
            await createVariable({
                type: 'ref',
                name,
                label,
                refTarget: 'User.name',
                refId: contactRefId,
                fixedValue: contactRefId ? undefined : contactFreeText.trim(),
            });
            await onSaved();
            resetContactForm();
        } catch {
            toast({ title: t('departmentVariables.errors.failedToAddContact'), variant: 'destructive' });
        }
    };

    const [customValueInput, setCustomValueInput] = useState('');

    const handleAddCustom = async () => {
        const label = customInput.trim();
        const value = customValueInput.trim();
        if (!label || !value) return;
        const name = toName(label);
        if (
            savedVariables.some((v) => v.name === name) ||
            defaultVariableNames.has(name)
        ) {
            toast({
                title: t('departmentVariables.errors.variableExists'),
                variant: 'destructive',
            });
            return;
        }
        try {
            await createVariable({
                type: 'fixed',
                name,
                label,
                fixedValue: value,
            });
            await onSaved();
            setCustomInput('');
            setCustomValueInput('');
            setShowAddCustom(false);
        } catch {
            toast({ title: t('departmentVariables.errors.failedToAddVariable'), variant: 'destructive' });
        }
    };

    const handleRemoveCustom = (name: string) => {
        removeVariable(name)
            .then(() => onSaved())
            .catch(() =>
                toast({
                    title: t('departmentVariables.errors.failedToRemoveVariable'),
                    variant: 'destructive',
                })
            );
    };

    const renderSection = (key: string) => {
        const section = sectionByKey[key];
        if (!section) return null;

        return (
            <SectionCard
                key={key}
                section={section}
                icon={SECTION_ICONS[key]}
                savedVariables={savedVariables}
                users={users}
                onSaved={onSaved}
            />
        );
    };

    return (
        <Tabs defaultValue="contacts">
            <TabsList className="mb-4">
                <TabsTrigger value="contacts" className="gap-1.5">
                    {t('departmentVariables.tabs.contacts')}
                </TabsTrigger>
                <TabsTrigger value="department" className="gap-1.5">
                    {t('departmentVariables.tabs.department')}
                </TabsTrigger>
                <TabsTrigger value="custom" className="gap-1.5">
                    {t('departmentVariables.tabs.custom')}
                </TabsTrigger>
            </TabsList>

            <TabsContent value="contacts" className="space-y-3">
                {renderSection('mriSafetyLeadership')}
                {renderSection('keyOperationalContacts')}
                {renderSection('optionalOperationalFields')}
            </TabsContent>

            <TabsContent value="department" className="space-y-3">
                {renderSection('departmentInformation')}
            </TabsContent>

            <TabsContent value="custom" className="space-y-3">
                <Card className="border-subtle overflow-hidden">
                    <div className="p-4 flex items-center gap-4">
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <Plus className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <span className="font-semibold">
                                {t('departmentVariables.custom.title')}
                            </span>
                            <p className="text-sm text-muted-foreground">
                                {t('departmentVariables.custom.description')}
                            </p>
                        </div>
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="gap-1.5 shrink-0"
                                >
                                    <Plus className="h-4 w-4" />
                                    {t('departmentVariables.custom.addButton')}
                                    <ChevronDown className="h-3 w-3" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                    onClick={() => {
                                        setShowAddContact(true);
                                        setShowAddCustom(false);
                                    }}
                                >
                                    <Users className="h-4 w-4" />
                                    {t('departmentVariables.custom.addContact')}
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                    onClick={() => {
                                        trackClick(
                                            EVENT_KEYS.clickAddVariable,
                                            {}
                                        );
                                        setShowAddCustom(true);
                                        setShowAddContact(false);
                                    }}
                                >
                                    <Settings className="h-4 w-4" />
                                    {t('departmentVariables.custom.addVariable')}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>

                    {customVariables.length === 0 &&
                    !showAddContact &&
                    !showAddCustom ? (
                        <div className="px-4 pb-4 border-t border-subtle">
                            <p className="text-sm text-muted-foreground/60 py-4 text-center">
                                {t('departmentVariables.custom.emptyState')}
                            </p>
                        </div>
                    ) : (
                        <div className="border-t border-subtle">
                            {customVariables.map((def) => {
                                const saved = savedVariables.find(
                                    (v) => v.name === def.name
                                );
                                return (
                                    <VariableRow
                                        key={def.name}
                                        def={def}
                                        savedVariable={saved}
                                        users={users}
                                        isCustom
                                        onSaved={onSaved}
                                        onRemove={() =>
                                            handleRemoveCustom(def.name)
                                        }
                                    />
                                );
                            })}
                        </div>
                    )}

                    {showAddContact && (
                        <div className="grid grid-cols-[1fr_2fr] gap-4 py-3 px-4 border-t border-subtle items-start">
                            <Input
                                placeholder={t('departmentVariables.custom.contactPlaceholder')}
                                value={contactInput}
                                onChange={(e) =>
                                    setContactInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') resetContactForm();
                                }}
                                autoFocus
                            />
                            <div className="space-y-2">
                                <RefVariablePicker
                                    options={users.map((u) => ({
                                        id: u.id,
                                        label: u.name,
                                        description: u.role,
                                    }))}
                                    selectedId={contactRefId}
                                    freeTextValue={contactFreeText}
                                    canClear
                                    placeholder={t('departmentVariables.custom.selectOrTypeName')}
                                    searchPlaceholder={t('departmentVariables.custom.searchTeamOrType')}
                                    emptyText={t('departmentVariables.custom.noTeamMembersFound')}
                                    onSelect={(id) => {
                                        setContactRefId(id);
                                        setContactFreeText('');
                                    }}
                                    onSelectFreeText={(text) => {
                                        setContactRefId(undefined);
                                        setContactFreeText(text);
                                    }}
                                    onClear={() => {
                                        setContactRefId(undefined);
                                        setContactFreeText('');
                                    }}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={resetContactForm}
                                    >
                                        {t('departmentVariables.variableRow.cancel')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={handleAddContact}
                                        disabled={
                                            !contactInput.trim() ||
                                            !hasContactValue
                                        }
                                    >
                                        {t('departmentVariables.custom.addButton')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}

                    {showAddCustom && (
                        <div className="grid grid-cols-[1fr_2fr] gap-4 py-3 px-4 border-t border-subtle items-start">
                            <Input
                                placeholder={t('departmentVariables.custom.variableLabelPlaceholder')}
                                value={customInput}
                                onChange={(e) => setCustomInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                        setShowAddCustom(false);
                                        setCustomInput('');
                                        setCustomValueInput('');
                                    }
                                }}
                                autoFocus
                            />
                            <div className="space-y-2">
                                <Input
                                    placeholder={t('departmentVariables.custom.valuePlaceholder')}
                                    value={customValueInput}
                                    onChange={(e) =>
                                        setCustomValueInput(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddCustom();
                                        }
                                        if (e.key === 'Escape') {
                                            setShowAddCustom(false);
                                            setCustomInput('');
                                            setCustomValueInput('');
                                        }
                                    }}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            setShowAddCustom(false);
                                            setCustomInput('');
                                            setCustomValueInput('');
                                        }}
                                    >
                                        {t('departmentVariables.variableRow.cancel')}
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={handleAddCustom}
                                        disabled={
                                            !customInput.trim() ||
                                            !customValueInput.trim()
                                        }
                                    >
                                        {t('departmentVariables.custom.addButton')}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )}
                </Card>
            </TabsContent>
        </Tabs>
    );
}
